import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function EducationDetail() {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResource();
  }, [id]);

  const fetchResource = async () => {
    try {
      const response = await axios.get(`${API}/education/${id}`);
      setResource(response.data);
    } catch (error) {
      console.error("Error fetching resource:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[#1B3A5F] text-lg">Loading...</div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-gray-600 mb-4">Resource not found</p>
        <Link to="/education">
          <Button className="bg-[#1B3A5F] hover:bg-[#2C5282]">
            Back to Education Center
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
        {/* Back Button */}
        <Link
          to="/education"
          className="inline-flex items-center text-[#1B3A5F] hover:text-[#2C5282] font-medium"
          data-testid="back-to-education-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Education Center
        </Link>

        {/* Article Header */}
        <div>
          <div className="flex items-center space-x-3 mb-4">
            <span className="px-4 py-1 bg-[#1B3A5F] text-white text-sm font-medium rounded-full">
              {resource.category}
            </span>
            <div className="flex items-center text-gray-600 text-sm">
              <Clock className="h-4 w-4 mr-1" />
              <span>{resource.read_time} min read</span>
            </div>
          </div>
          
          <h1 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
            {resource.title}
          </h1>
          
          <p className="text-xl text-gray-600">
            {resource.description}
          </p>
        </div>

        {/* Featured Image */}
        <div className="rounded-lg overflow-hidden shadow-lg">
          <img
            src={resource.image_url}
            alt={resource.title}
            className="w-full h-96 object-cover"
          />
        </div>

        {/* Article Content */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 lg:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed text-lg">
              {resource.content}
            </p>
            
            {/* Extended sample content */}
            <div className="mt-6 space-y-4 text-gray-700">
              <h2 className="heading-font text-2xl font-semibold text-[#1B3A5F] mt-8 mb-4">
                Key Takeaways
              </h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>Understanding the fundamentals is crucial for long-term success</li>
                <li>Start with small, manageable steps to build confidence</li>
                <li>Regular review and adjustment of your strategy is essential</li>
                <li>Don't hesitate to seek professional guidance when needed</li>
              </ul>

              <h2 className="heading-font text-2xl font-semibold text-[#1B3A5F] mt-8 mb-4">
                Getting Started
              </h2>
              <p>
                The journey to financial independence starts with education and action. 
                At Anchor Lane, we believe that everyone deserves access to quality 
                financial guidance. Our team is here to support you every step of the way.
              </p>

              <h2 className="heading-font text-2xl font-semibold text-[#1B3A5F] mt-8 mb-4">
                Next Steps
              </h2>
              <p>
                Ready to put this knowledge into practice? Consider scheduling a 
                consultation with one of our financial advisors. We'll work together 
                to create a personalized plan that aligns with your goals and values.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#1B3A5F] text-white rounded-lg shadow-md p-8" data-testid="article-cta">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="heading-font text-2xl font-bold mb-2">
                Ready to Take Action?
              </h3>
              <p className="text-white/80">
                Schedule a personalized consultation with our experts
              </p>
            </div>
            <Link to="/appointments">
              <Button className="bg-[#C5A059] hover:bg-[#9F8045] text-white">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
